import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
import SisCancelExecution from "./SisCancelExecution";
import SisRepostPreview from "./SisRepostPreview";
const today = () => new Date(Date.now() + 19800000).toISOString().slice(0, 10);
export default function SisCorrectionReview({
  company,
  saleId,
  accessToken,
  canRequest = false,
  canExecute = false,
  onCancelled,
  ledgers = [],
}) {
  const [date, setDate] = useState(today),
    [result, setResult] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [history, setHistory] = useState([]),
    [intent, setIntent] = useState("CANCEL_ONLY"),
    [reason, setReason] = useState(""),
    [evidence, setEvidence] = useState(""),
    [replacement, setReplacement] = useState(""),
    [checks, setChecks] = useState({}),
    [notice, setNotice] = useState("");
  const requestKey = useRef(null);
  function changed() {
    requestKey.current = null;
    setChecks({});
    setNotice("");
  }
  const epoch = useRef(0),
    lock = useRef(false);
  useEffect(() => {
    epoch.current++;
    setResult(null);
    setError("");
    setBusy(false);
    lock.current = false;
    setDate(today());
    setHistory([]);
    setIntent("CANCEL_ONLY");
    setReason("");
    setEvidence("");
    setReplacement("");
    changed();
    let active = true;
    apiRequest(
      `/accounting/${company}/supplier-liabilities/sis/pos/${saleId}/correction-requests`,
      { accessToken },
    )
      .then((r) => {
        if (active) setHistory(Array.isArray(r) ? r : []);
      })
      .catch(() => {
        if (active)
          setError(
            "Unable to load retained request history. Refresh before continuing.",
          );
      });
    return () => {
      active = false;
      epoch.current++;
    };
  }, [company, saleId, accessToken]);
  async function review(e) {
    e.preventDefault();
    if (lock.current || !date) return;
    changed();
    const ticket = epoch.current;
    lock.current = true;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const r = await apiRequest(
        `/accounting/${company}/supplier-liabilities/sis/pos/${saleId}/correction-review?date=${encodeURIComponent(date)}`,
        { accessToken },
      );
      if (ticket === epoch.current) setResult(r);
    } catch (e) {
      if (ticket === epoch.current)
        setError(e.message || "Unable to review correction dependencies");
    } finally {
      if (ticket === epoch.current) {
        setBusy(false);
        lock.current = false;
      }
    }
  }
  async function retain() {
    if (
      lock.current ||
      !result ||
      !canRequest ||
      !checks.requestOnly ||
      !checks.historyReviewed
    )
      return;
    const ticket = epoch.current;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      if (!window.crypto?.randomUUID)
        throw Error("Secure UUID support is required");
      if (!requestKey.current) requestKey.current = window.crypto.randomUUID();
      const row = await apiRequest(
        `/accounting/${company}/supplier-liabilities/sis/pos/${saleId}/correction-requests`,
        {
          method: "POST",
          accessToken,
          body: {
            requestKey: requestKey.current,
            reviewHash: result.reviewHash,
            reviewDate: date,
            intent,
            reason,
            evidenceReference: evidence,
            replacementDescription: intent === "CANCEL_ONLY" ? "" : replacement,
            requestOnly: true,
            historyReviewed: true,
          },
        },
      );
      if (ticket === epoch.current) {
        setHistory((h) => [row, ...h.filter((x) => x.id !== row.id)]);
        setResult(null);
        changed();
        setNotice(
          "Correction request retained. The original accrual, supplier dues, stock and payments are unchanged. Nothing was cancelled or reposted.",
        );
      }
    } catch (e) {
      if (ticket === epoch.current)
        setError(
          e.message ||
            "Unable to retain request. Retry without changing inputs to reuse the same request key.",
        );
    } finally {
      if (ticket === epoch.current) {
        setBusy(false);
        lock.current = false;
      }
    }
  }
  return (
    <section className="space-y-4 rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold">
        Bookkeeping correction review without a goods return
      </h3>
      <p className="text-sm">
        Use this for an erroneous SIS accrual, not a customer returning stock.
        Review the original compensating amounts and downstream links. Eligible
        retained requests have separate cancel-only or expense-ledger correction
        controls below. Supplier amounts/terms cannot be changed; never confirm
        a physical return just to bypass an accounting lock.
      </p>
      <form onSubmit={review} className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          Correction review date in India
          <input
            required
            type="date"
            max={today()}
            disabled={busy}
            className="mt-1 block rounded border p-2"
            value={date}
            onChange={(e) => {
              epoch.current++;
              setDate(e.target.value);
              setResult(null);
              setError("");
              changed();
            }}
          />
        </label>
        <button
          disabled={busy || !date}
          className="rounded-lg border px-4 py-2 disabled:opacity-40"
        >
          {busy ? "Checking dependencies…" : "Review bookkeeping correction"}
        </button>
      </form>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-sm text-green-800">
          {notice}
        </p>
      )}
      {result && (
        <div className="space-y-3">
          <p className="text-sm">{result.warning}</p>
          <p className="font-medium">
            Original {result.snapshot.originalNumber} ·{" "}
            {result.snapshot.originalDate} · Gross ₹
            {result.snapshot.originalGross}
          </p>
          <p className="text-sm">
            Review date {result.snapshot.reviewDate}. Retained bill allocation
            claims: ₹{result.snapshot.dependencies.claimedAllocationAmount}. Not
            a refund or an unpaid-balance calculation.
          </p>
          <h4 className="font-medium">
            Blocking dependencies and execution gates
          </h4>
          <ul className="list-disc space-y-2 pl-5 text-sm">
            {result.snapshot.blockers.map((b) => (
              <li key={b.code}>
                <strong>{b.code.replaceAll("_", " ")}</strong>
                {b.count > 1 ? ` (${b.count})` : ""}: {b.detail}
              </li>
            ))}
          </ul>
          <details>
            <summary className="cursor-pointer font-medium">
              Original reversal amounts for review only
            </summary>
            <div className="mt-3 overflow-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr>
                    <th className="p-2">Ledger</th>
                    <th className="p-2">Debit</th>
                    <th className="p-2">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {result.snapshot.reversalLines.map((l, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">
                        {l.ledgerCode} · {l.ledgerName}
                      </td>
                      <td className="p-2">₹{l.debit}</td>
                      <td className="p-2">₹{l.credit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-sm">
              These reverse original amounts only. Corrected supplier dues and
              replacement terms are not inferred or posted.
            </p>
          </details>
          <details>
            <summary className="cursor-pointer text-sm">
              Linked invoice and credit references
            </summary>
            <ul className="mt-2 space-y-1 text-sm">
              {result.snapshot.dependencies.invoices.map((i) => (
                <li key={i.id}>Supplier invoice {i.invoiceNumber}</li>
              ))}
              {result.snapshot.dependencies.returns.map((r) => (
                <li key={r.id}>Retained SIS return {r.id}</li>
              ))}
            </ul>
          </details>
          <p className="text-sm font-semibold">
            Read only. No cancellation, repost, stock movement, payment or
            refund has been performed.
          </p>
          {canRequest && !history.some((r) => r.cancellation) && (
            <fieldset
              disabled={busy}
              className="space-y-3 rounded-lg border p-4"
            >
              <legend className="font-medium">
                Retain a correction request only
              </legend>
              <p className="text-sm">
                This creates immutable request history, not an approval,
                voucher, cancellation or reservation of supplier dues. Existing
                financial blockers remain. Later versions preserve earlier
                requests.
              </p>
              <label className="block text-sm">
                Requested correction
                <select
                  className="mt-1 block w-full rounded border p-2"
                  value={intent}
                  onChange={(e) => {
                    setIntent(e.target.value);
                    setReplacement("");
                    changed();
                  }}
                >
                  <option value="CANCEL_ONLY">Cancel only</option>
                  <option value="CANCEL_AND_REPOST">
                    Cancel and repost after separate review
                  </option>
                </select>
              </label>
              <label className="block text-sm">
                Bookkeeping correction reason
                <textarea
                  className="mt-1 block w-full rounded border p-2"
                  minLength={20}
                  maxLength={1000}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    changed();
                  }}
                />
              </label>
              <label className="block text-sm">
                Correction evidence reference
                <input
                  className="mt-1 block w-full rounded border p-2"
                  maxLength={200}
                  value={evidence}
                  onChange={(e) => {
                    setEvidence(e.target.value);
                    changed();
                  }}
                />
              </label>
              {intent === "CANCEL_AND_REPOST" && (
                <label className="block text-sm">
                  Proposed replacement explanation
                  <textarea
                    className="mt-1 block w-full rounded border p-2"
                    maxLength={1000}
                    value={replacement}
                    onChange={(e) => {
                      setReplacement(e.target.value);
                      changed();
                    }}
                  />
                </label>
              )}
              <label className="flex gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!checks.historyReviewed}
                  onChange={(e) =>
                    setChecks((x) => ({
                      ...x,
                      historyReviewed: e.target.checked,
                    }))
                  }
                />
                I reviewed the original history and linked document blockers.
              </label>
              <label className="flex gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!checks.requestOnly}
                  onChange={(e) =>
                    setChecks((x) => ({ ...x, requestOnly: e.target.checked }))
                  }
                />
                I understand this saves a request only and does not change
                financial records.
              </label>
              <button
                type="button"
                onClick={retain}
                disabled={
                  busy ||
                  reason.trim().length < 20 ||
                  evidence.trim().length < 3 ||
                  (intent === "CANCEL_AND_REPOST" &&
                    replacement.trim().length < 20) ||
                  !checks.requestOnly ||
                  !checks.historyReviewed
                }
                className="rounded border px-4 py-2 disabled:opacity-40"
              >
                Retain correction request only
              </button>
            </fieldset>
          )}
        </div>
      )}
      {!!history.length && (
        <div className="space-y-2">
          <h4 className="font-medium">
            Recent retained correction requests up to 50
          </h4>
          {history.map((r, i) => (
            <div key={r.id} className="rounded border p-3 text-sm">
              <p>
                Version {r.version} ·{" "}
                {r.intent === "CANCEL_ONLY"
                  ? "Cancel only requested"
                  : "Cancel and repost requested"}{" "}
                ·{" "}
                {r.ledgerCorrection ? 'EXPENSE LEDGER CORRECTED' : r.status === "CANCELLED"
                  ? "CANCELLED"
                  : r.cancellation
                    ? "ORIGINAL ACCRUAL CANCELLED"
                    : "REQUESTED NOT POSTED"}
              </p>
              <p>{r.reason}</p>
              <p>
                Evidence: {r.evidenceReference} · Review date:{" "}
                {String(r.reviewDate).slice(0, 10)}
              </p>
              {r.snapshot?.replacementDescription && (
                <p>Proposed replacement: {r.snapshot.replacementDescription}</p>
              )}
              <p>
                {r.ledgerCorrection ? `Expense correction voucher ${r.ledgerCorrection.number||r.ledgerCorrection.voucherId}. Original accrual and supplier dues unchanged.` : r.cancellation
                  ? `Cancellation voucher ${r.cancellation.number || r.cancellation.voucherId} · ${String(r.cancellation.date).slice(0, 10)}. No replacement posted.`
                  : "No financial correction executed."}
              </p>
              {i === 0 && r.intent === "CANCEL_ONLY" && !r.cancellation && !r.ledgerCorrection && (
                <SisCancelExecution
                  company={company}
                  saleId={saleId}
                  requestId={r.id}
                  accessToken={accessToken}
                  canExecute={canExecute}
                  onCancelled={(row) => {
                    setHistory((h) =>
                      h.map((x) => ({
                        ...x,
                        status:
                          x.id === r.id
                            ? "CANCELLED"
                            : "ACCRUAL_CANCELLED_VIA_OTHER_REQUEST",
                        cancellation: {
                          id: row.id,
                          voucherId: row.voucherId,
                          date: row.date,
                          number: row.snapshot.reversalNumber,
                        },
                      })),
                    );
                    setResult(null);
                    setNotice(
                      "Original SIS accrual cancelled by an audited reversal. Original history is preserved. No stock, payment, refund or replacement accrual was created.",
                    );
                    if (onCancelled) onCancelled(row);
                  }}
                />
              )}
              {i === 0 && r.intent === 'CANCEL_AND_REPOST' && !r.cancellation && !r.ledgerCorrection && <SisRepostPreview company={company} saleId={saleId} requestId={r.id} accessToken={accessToken} ledgers={ledgers} canExecute={canExecute} onCorrected={row=>{setHistory(h=>h.map(x=>({...x,status:x.id===r.id?'EXPENSE_LEDGER_CORRECTED':'EXPENSE_CORRECTED_VIA_OTHER_REQUEST',ledgerCorrection:{id:row.id,voucherId:row.voucherId,date:row.date,number:row.snapshot.correctionNumber}})));setResult(null);setNotice('Expense correction posted. Original accrual, supplier dues, bills and stock are unchanged.');}}/>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
